import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component, EventEmitter, Inject,
  Input, OnChanges, OnInit, Output, SimpleChanges
} from "@angular/core";
import {VisitPlanAccountFacadeService} from "../../services/visit-plan-account-facade.service";
import {BringozTimeConvertPipe} from "../../../../commons/angular/pipes/bringoz-time-convert.pipe";
import * as _ from "lodash";
import {BringozTimePipe} from "../../../../commons/angular/pipes/bringoz-time.pipe";

type ChangeRecord = {
  path: string;
  key: string;
  previous: any;
  pending: any;
  callType: string;
};


require('./visit-plan-tracker-changes-view.scss');
@Component({
  standalone: false,
  selector: 'app-visit-plan-tracker-changes-view',
  templateUrl: './visit-plan-tracker-changes-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BringozTimeConvertPipe, BringozTimePipe]
})
export class VisitPlanTrackerChangesViewComponent implements OnInit, OnChanges {

  @Input() visitPlanId: string;
  @Input() excludeMode: boolean;
  @Input() selectedIds: string[];
  @Output() onSelectedIdsChange = new EventEmitter<{ selectedIds: string[], excludeMode: boolean }>();

  page: number = 0;
  size: number = 20;
  paginationData: any;

  accountChangesMap: { [key: string]: any } = {};
  siteProgramTypesMap: { [key: string]: any } = {};
  selectedKeys: { [key: string]: boolean } = {};
  isLoadingData: boolean = false;

  constructor(@Inject(VisitPlanAccountFacadeService) private visitPlanAccountFacadeService: VisitPlanAccountFacadeService,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef, @Inject(BringozTimeConvertPipe) private bringozTimeConvertPipe: BringozTimeConvertPipe,
              @Inject('splitService') private splitService, @Inject(BringozTimePipe) private bringozTimePipe: BringozTimePipe) {
  }

  ngOnInit() {
    this.siteProgramTypesMap = {...this.splitService.getSiteProgramTypesMap(), delivery: 'Delivery'};
    this._getVisitPlanTracker();

    this.initSelectedKeysMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedIds && !changes.selectedIds.isFirstChange()) {
      this.initSelectedKeysMap();
    }
  }


  onPaginationChange(pageIndex) {
    this.page = pageIndex;
    this._getVisitPlanTracker();
  }

  onToggleSelectAll() {
    this.onSelectedIdsChange.emit({
      selectedIds: [],
      excludeMode: !this.excludeMode
    });
  }

  onToggleSelect(change) {
    this.selectedKeys[change.id] = !this.selectedKeys[change.id];
    this.onSelectedIdsChange.emit({
      selectedIds: _.keys(_.pickBy(this.selectedKeys, _.identity)),
      excludeMode: this.excludeMode
    });
  }

  private initSelectedKeysMap() {
    this.selectedKeys = {};
    _.forEach(this.selectedIds, (selectedId) => {
      this.selectedKeys[selectedId] = true;
    });
  }

  private _getVisitPlanTracker() {
    this.isLoadingData = true;
    this.visitPlanAccountFacadeService.getTrackerChanges(this.visitPlanId, {page: this.page, size: this.size})
      .subscribe({
        next: (paginationData) => {
          this.paginationData = paginationData;
          this.accountChangesMap = this._initAccountChangeMap(paginationData.content);
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoadingData = false;
          this.cdr.detectChanges();
        },
        complete: () => {
          this.isLoadingData = false;
          this.cdr.detectChanges();
        }
      });
  }

  private _initAccountChangeMap(accounts: any[]): Record<number, ChangeRecord[]> {
    const result: Record<number, ChangeRecord[]> = {};

    accounts.forEach((account, index) => {
      const changes: ChangeRecord[] = [];

      // Handle calls
      (account.calls || []).forEach((call, idx) => {
        this._collectChanges(call, `call[${idx}]`, changes, `call[${idx}]`, call.programTypeValue);
      });

      // Handle deliveryDetails
      if (account.deliveryDetails) {
        const delivery = account.deliveryDetails;

        // isDeliveryDay
        this._collectChanges(
          delivery.isDeliveryDay,
          `deliveryDetails.isDeliveryDay`,
          changes,
          `isDeliveryDay`,
          'delivery'
        );

        // deliveryUnits
        this._collectChanges(
          delivery.deliveryUnits,
          `deliveryDetails.deliveryUnits`,
          changes,
          'deliveryUnits',
          'delivery'
        );

      }

      if (changes.length) {
        result[index] = changes;
      }
    });

    return result;
  }

  private _collectChanges(node: any, path: string, out: ChangeRecord[], key: string = '', programName: string): void {
    if (node == null) return;

    if (
      typeof node === 'object' &&
      !Array.isArray(node) &&
      'initial' in node &&
      'pending' in node
    ) {
      let previous;
      let pending;
      if (key === 'serviceTime') {
        previous = node.adjusted?.value ? this.bringozTimeConvertPipe.transform(node.adjusted.value.generatedTotalTime, true) : this.bringozTimeConvertPipe.transform(node.initial.value.generatedTotalTime, true);
        pending = this.bringozTimeConvertPipe.transform(node.pending.value.generatedTotalTime, true);
      } else if (key === 'subtasks') {
        previous = !!node.adjusted?.value ? node.adjusted.value.map(obj => obj.title).join(", ") : node.initial.value.map(obj => obj.title).join(", ");
        pending = node.pending.value.map(obj => obj.title).join(", ");
      } else if (key === 'notBefore' || key === 'notAfter') {
        if (key === 'notBefore') {
          previous = node.adjusted?.value ? this.bringozTimePipe.transform(node.adjusted, 'value') : this.bringozTimePipe.transform(node.initial, 'value');
          pending = this.bringozTimePipe.transform(node.pending, 'value');
        } else {
          previous = node.adjusted?.value ? this.bringozTimePipe.transform(node.adjusted, 'value') : this.bringozTimePipe.transform(node.initial, 'value');
          pending = this.bringozTimePipe.transform(node.pending, 'value');
        }
      } else {
        previous = node.adjusted?.value !== undefined
          ? (typeof node.adjusted.value === 'boolean' ? (node.adjusted.value ? 'Yes' : 'No') : node.adjusted.value)
          : (typeof node.initial.value === 'boolean' ? (node.initial.value ? 'Yes' : 'No') : node.initial.value);

        pending = typeof node.pending.value === 'boolean'
          ? (node.pending.value ? 'Yes' : 'No')
          : node.pending.value;
      }
      out.push({path, key: key, previous: previous, pending: pending, callType: programName});
      return;
    }

    if (Array.isArray(node)) {
      node.forEach((item, idx) => {
        this._collectChanges(item, `${path}[${idx}]`, out, `${key}[${idx}]`, programName);
      });
      return;
    }

    if (typeof node === 'object') {
      for (const key of Object.keys(node)) {
        const nextPath = path ? `${path}.${key}` : key;
        this._collectChanges(node[key], nextPath, out, key, programName);
      }
    }
  }


}
